/**
 * Product Guided Nav
 * Syncs nav anchors with target sections via Intersection Observer.
 * Hides anchors whose target ID is missing; adds/removes selected class when target is in view.
 * Applies fixed positioning when section scrolls out of view so nav sticks below header.
 * Header offset is synced from sticky-header-wrapper height (includes GWP bar collapse/expand).
 */
(function productGuidedNav() {
  const SELECTED_CLASS = "product-guided-nav-item--selected";
  const STUCK_CLASS = "product-guided-nav--stuck";

  const HEADER_OFFSET_FALLBACK = 92;

  /**
   * @param {HTMLElement} wrapper
   * @returns {number}
   */
  function getHeaderOffset(wrapper) {
    const offsetVal = getComputedStyle(wrapper).getPropertyValue("--guided-nav-header-offset").trim();
    return offsetVal ? parseFloat(offsetVal) : HEADER_OFFSET_FALLBACK;
  }

  /**
   * Sync --guided-nav-header-offset from sticky-header-wrapper height (header + GWP bar).
   * @param {HTMLElement} wrapper
   */
  function updateHeaderOffset(wrapper) {
    const stickyHeader = document.querySelector("sticky-header-wrapper");
    if (!(stickyHeader instanceof HTMLElement)) return;

    wrapper.style.setProperty("--guided-nav-header-offset", `${stickyHeader.offsetHeight}px`);
  }

  /**
   * @param {HTMLElement} wrapper
   */
  function initSticky(wrapper) {
    const bar = wrapper.querySelector("[data-guided-nav-bar]");
    if (!bar) return;

    const stickyHeader = document.querySelector("sticky-header-wrapper");
    /** @type {ResizeObserver|null} */
    let headerResizeObserver = null;

    let ticking = false;

    function updateStuck() {
      if (!bar) {
        ticking = false;
        return;
      }
      const headerOffset = getHeaderOffset(wrapper);
      const rect = wrapper.getBoundingClientRect();
      const isStuck = rect.top <= headerOffset;
      bar.classList.toggle(STUCK_CLASS, isStuck);
      if (isStuck && bar instanceof HTMLElement) {
        wrapper.style.minHeight = `${bar.offsetHeight}px`;
      } else {
        wrapper.style.minHeight = "";
      }
      ticking = false;
    }

    function onFrame() {
      updateHeaderOffset(wrapper);
      updateStuck();
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(onFrame);
    }

    updateHeaderOffset(wrapper);
    updateStuck();

    if (stickyHeader instanceof HTMLElement && typeof ResizeObserver !== "undefined") {
      headerResizeObserver = new ResizeObserver(() => onScroll());
      headerResizeObserver.observe(stickyHeader);
    }

    window.addEventListener("scroll", onScroll, { passive: true });
  }

  function init() {
    const navAnchors = document.querySelectorAll("[data-guided-nav-id]");
    const wrapper = document.querySelector(".product-guided-nav-wrapper");

    if (wrapper instanceof HTMLElement) {
      initSticky(wrapper);
    }

    if (!navAnchors.length) return;

    function clearAriaCurrentFromAnchors() {
      navAnchors.forEach(a => {
        if (a instanceof HTMLElement) a.removeAttribute("aria-current");
      });
    }

    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          const navId = entry.target.id;
          if (!navId) continue;
          const anchor = document.querySelector(`[data-guided-nav-id="${navId}"]`);
          if (!anchor) continue;
          const isIntersecting = entry.isIntersecting;
          anchor.classList.toggle(SELECTED_CLASS, isIntersecting);
          if (isIntersecting) {
            clearAriaCurrentFromAnchors();
            if (anchor instanceof HTMLElement) anchor.setAttribute("aria-current", "true");
          } else if (anchor instanceof HTMLElement) {
            anchor.removeAttribute("aria-current");
          }
        }
      },
      { rootMargin: "0px", threshold: 0.2 }
    );

    for (const anchor of navAnchors) {
      const navId = anchor.getAttribute("data-guided-nav-id");
      if (!navId) continue;

      const targetEl = document.getElementById(navId);
      if (!targetEl) {
        if (anchor instanceof HTMLElement) anchor.hidden = true;
        continue;
      }

      observer.observe(targetEl);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
